import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, setToken } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * AuthCallback — one-time processor for the `#session_id=…` fragment that
 * Emergent Auth appends to our redirect URL after a Google sign-in.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH.
 */
export default function AuthCallback() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;

    if (!sessionId) {
      toast.error("No Google session — please try again");
      nav("/login", { replace: true });
      return;
    }

    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", { session_id: sessionId });
        setToken(data.token);
        // Strip the hash from the URL so a refresh doesn't re-run this
        window.history.replaceState(null, "", "/console/dashboard");
        await refresh();
        toast.success(`Welcome, ${data.user?.name || "operator"}`);
        nav("/console/dashboard", { replace: true });
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Google sign-in failed");
        nav("/login", { replace: true });
      }
    })();
  }, [nav, refresh]);

  return (
    <div className="min-h-screen bg-background text-foreground grain flex items-center justify-center">
      <div className="glass-card p-8 text-center max-w-sm">
        <Loader2 className="w-8 h-8 animate-spin text-fuchsia-400 mx-auto" strokeWidth={1.5} />
        <div className="mt-4 font-display font-bold text-lg">Signing you in…</div>
        <div className="mt-1 text-sm text-muted-foreground">Provisioning your workspace with Google.</div>
      </div>
    </div>
  );
}
